import { useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { qk } from '@/shared/api/query-keys'

import { CompanyMonogram, StatusPill } from '@/shared/components/common'
import { Button, Field, Input, Select, Sheet, Switch, Textarea } from '@/shared/components/ui'
import { useAssignableUsers } from '@/shared/hooks/use-users'
import { useI18n } from '@/shared/i18n'
import type { Stage, Task, TaskPatchInput } from '@/shared/types/domain'

import { useTaskNotes } from '../api'
import { AssigneePicker } from './AssigneePicker'
import { DoneToggle } from './DoneToggle'
import { DueDatePicker } from './DueDatePicker'

export interface TaskDetailSheetProps {
  task: Task | null
  stages: readonly Stage[]
  onClose: () => void
  onPatch: (task: Task, patch: TaskPatchInput, optimistic: (current: Task) => Task) => void
  /**
   * Cambiar de columna es `POST /tasks/{id}/move`, no un campo del PATCH: el
   * movimiento lleva vecinos y el rango lo calcula el servidor.
   */
  onMoveToStage: (task: Task, stage: Stage) => void
  /**
   * Completar o reabrir **la tarea abierta**, desde el propio panel.
   *
   * Faltaba: el pie decía `Sin completar`, que es una etiqueta y no un botón,
   * así que la acción más repetida del día —marcar hecha la tarea que uno
   * acaba de leer— obligaba a cerrar el panel y buscar su fila. El QA funcional
   * lo levantó como 1.6.
   */
  onToggleDone: (task: Task, done: boolean) => void
  onDelete: (task: Task) => void
}

/**
 * Detalle de una tarea en un panel lateral.
 *
 * Se abre SIN cambiar de ruta a proposito: la lista de atras conserva su
 * scroll, sus filtros y su cache, y cerrar el panel no cuesta una navegacion.
 */
export function TaskDetailSheet({
  task,
  stages,
  onClose,
  onPatch,
  onMoveToStage,
  onToggleDone,
  onDelete,
}: TaskDetailSheetProps) {
  const { t, formatDateTime } = useI18n()
  // Los mismos que ofrece el desplegable: el update optimista no puede pintar
  // un nombre que el selector no ofrecía.
  const { data: users = [] } = useAssignableUsers(task?.project?.id ?? null)
  const client = useQueryClient()
  // Las notas no vienen en la fila de la lista: se leen de la tarea.
  const notas = useTaskNotes(task?.id ?? null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    setTitle(task?.title ?? '')
  }, [task])

  useEffect(() => {
    setDescription(notas.data ?? '')
  }, [task?.id, notas.data])

  if (!task) return null

  const patch = (changes: TaskPatchInput, optimistic: (current: Task) => Task) =>
    onPatch(task, changes, optimistic)

  return (
    <Sheet
      open
      onClose={onClose}
      title={
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-2xs text-text-3">
            <CompanyMonogram
              name={task.project.company.name}
              hue={task.project.company.color_hue}
            />
            <span className="truncate">{task.project.company.name}</span>
            <span aria-hidden="true">·</span>
            <span className="truncate">{task.project.name}</span>
          </div>
          {/* El nombre de la fase lo pone cada empresa: no se traduce. */}
          <StatusPill category={task.stage.category}>{task.stage.name}</StatusPill>
        </div>
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          {/*
            La acción, no sólo el estado.

            Acá decía `Sin completar` y nada más: una etiqueta donde hacía falta
            un botón. La casilla es la misma de la fila —mismo control, mismo
            `aria-checked`, misma mutación— para que completar signifique lo
            mismo en los dos lugares.

            El backend no devuelve `updated_at` en las tareas; lo que sí sabemos
            es cuándo se completó, y eso sigue al lado.
          */}
          <span className="flex min-w-0 items-center gap-2">
            <DoneToggle
              done={task.completed_at !== null}
              title={task.title}
              onChange={(done) => onToggleDone(task, done)}
            />
            <span className="num truncate text-2xs text-text-3">
              {task.completed_at
                ? t('tasks.detail.completedAt', { fecha: formatDateTime(task.completed_at) })
                : t('tasks.detail.notCompleted')}
            </span>
          </span>
          <Button
            variant="danger"
            onClick={() => {
              onDelete(task)
              onClose()
            }}
          >
            <Trash2 size={13} aria-hidden="true" />
            {t('common.action.delete')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('common.label.title')} htmlFor="task-title">
          <Input
            id="task-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onBlur={() => {
              const clean = title.trim()
              if (!clean || clean === task.title) return
              patch({ title: clean }, (current) => ({ ...current, title: clean }))
            }}
          />
        </Field>

        <Field label={t('common.label.status')} htmlFor="task-stage">
          <Select
            id="task-stage"
            value={task.stage.id}
            onChange={(event) => {
              const stage = stages.find((candidate) => candidate.id === event.target.value)
              if (!stage) return
              onMoveToStage(task, stage)
            }}
          >
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('common.label.owner')}>
          <AssigneePicker
            className="w-full"
            projectId={task.project?.id ?? null}
            value={task.assignee?.id ?? null}
            onChange={(assigneeId) =>
              patch({ assignee_id: assigneeId }, (current) => ({
                ...current,
                assignee: users.find((user) => user.id === assigneeId) ?? null,
              }))
            }
          />
        </Field>

        <Field label={t('tasks.field.dueDate')}>
          <DueDatePicker
            withShortcuts
            value={task.due_date}
            onChange={(due) =>
              patch({ due_date: due }, (current) => ({ ...current, due_date: due }))
            }
          />
        </Field>

        <Field
          label={t('common.label.notes')}
          htmlFor="task-description"
          hint={t('tasks.detail.notesHint')}
        >
          <Textarea
            id="task-description"
            rows={6}
            value={description}
            // Hasta que llegan las notas no se escribe: se pisaría lo que hay.
            disabled={notas.data === undefined}
            onChange={(event) => setDescription(event.target.value)}
            onBlur={() => {
              if (notas.data === undefined || description === notas.data) return
              client.setQueryData(qk.tasks.detail(task.id), description)
              patch({ description_md: description }, (current) => ({
                ...current,
                description_md: description,
              }))
            }}
          />
        </Field>

        <Switch
          label={t('tasks.detail.clientVisible')}
          checked={task.is_client_visible}
          onChange={(checked) =>
            patch({ is_client_visible: checked }, (current) => ({
              ...current,
              is_client_visible: checked,
            }))
          }
        />
      </div>
    </Sheet>
  )
}
